import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { computed, ref } from 'vue'
import { type Note } from '@/types'

const useEventNotesStoreInner = defineStore('eventNotesInner', () => {
	const notes = ref<Note[]>([])
	const isAddingNote = ref(false)
	const editedNote = ref<Note>()
	const noteBeingDeleted = ref<Note>()

	return {
		notes,
		isAddingNote,
		editedNote,
		noteBeingDeleted
	}
})

export const useEventNotesStore = defineStore('eventNotes', () => {
	const innerStore = useEventNotesStoreInner()

	const notes = computed(() => [...innerStore.notes].sort((a, b) => b.create_time.toMillis() - a.create_time.toMillis()))
	const upsertedNoteText = ref('')
	const isAddingNote = computed(() => innerStore.isAddingNote)
	const editedNote = computed(() => innerStore.editedNote)
	const noteBeingDeleted = computed(() => innerStore.noteBeingDeleted)
	const isUpserting = computed(() => isAddingNote.value || !!editedNote.value)

	function setNotes(notes: Note[]) {
		innerStore.notes = notes
	}

	function onAddNote() {
		clear()

		innerStore.isAddingNote = true
	}

	function onEditNote(note: Note) {
		clear()

		innerStore.editedNote = note
		upsertedNoteText.value = note.text
	}

	function onRemoveNote(note: Note) {
		innerStore.noteBeingDeleted = note
	}

	function onConfirmNoteRemoval() {
		innerStore.notes = innerStore.notes.filter((note) => note !== noteBeingDeleted.value)

		clear()
	}

	function save() {
		if (innerStore.editedNote) {
			innerStore.editedNote.text = upsertedNoteText.value
			innerStore.editedNote.update_time = DateTime.now()

			clear()

			return
		}

		innerStore.notes.push({
			id: uuidv4(),
			text: upsertedNoteText.value,
			create_time: DateTime.now()
		})

		clear()
	}

	function clear(includeNotes?: boolean) {
		upsertedNoteText.value = ''
		innerStore.isAddingNote = false
		innerStore.editedNote = undefined
		innerStore.noteBeingDeleted = undefined

		if (includeNotes) {
			setNotes([])
		}
	}

	return {
		notes,
		isAddingNote,
		isUpserting,
		upsertedNoteText,
		editedNote,
		noteBeingDeleted,
		setNotes,
		onAddNote,
		onEditNote,
		onRemoveNote,
		onConfirmNoteRemoval,
		save,
		cancel: clear
	}
})
